import React, { useCallback, type AnchorHTMLAttributes, type ReactNode, type Ref } from 'react';
import { RomaRouterProvider, type RenderRouterLink, type RouterOptions } from '@milanote/roma';
import { Link } from '../../node_module_clones/react-router';

type RouterHistory = {
    getCurrentLocation: () => { search: string };
};

type RouterLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    to: string;
    innerRef?: Ref<HTMLAnchorElement>;
};

const RouterLink = Link as React.ComponentType<RouterLinkProps>;

/** Delegates internal links to React Router while preserving native link behaviours. */
const renderRouterLink: RenderRouterLink = (props) => {
    if (!('href' in props)) return <span {...props} />;

    const { href, ref, ...linkProps } = props;
    const useNativeLink =
        typeof href !== 'string' || !href.startsWith('/') || href.startsWith('//') || props.download != null;

    if (useNativeLink) return <a {...linkProps} href={href} ref={ref} />;

    return <RouterLink {...linkProps} to={href} innerRef={ref as Ref<HTMLAnchorElement>} />;
};

/** Connects router-neutral Roma links to the Milanote client router. */
export const RomaRouterBridge: React.FC<{ children: ReactNode; history: RouterHistory }> = ({ children, history }) => {
    const resolveHref = useCallback(
        (href: string, options?: RouterOptions) =>
            options?.keepParams ? `${href}${history.getCurrentLocation().search}` : href,
        [history],
    );

    return (
        <RomaRouterProvider renderLink={renderRouterLink} resolveHref={resolveHref}>
            {children}
        </RomaRouterProvider>
    );
};
